import { Link } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { IoMenuSharp } from "react-icons/io5";
import FocusLock from "react-focus-lock";
import { useEffect } from "react";

// icons
import { SBSLogo, GetHelp, HelpGuide } from "components/icons";
import { GovBanner, Icon } from "@trussworks/react-uswds";

// styles
import styles from "./Header.module.scss";

// store
import { useResponsiveStore } from "store/useResponsiveStore";
import useAuthStore from "store/useAuthStore";

// components
import {
  Button,
  RoundedAnchorButton,
  RoundedButton,
} from "components/LibraryComponents/Components";
import Menu from "./Menu/Menu";
import { IconButton } from "components/LibraryComponents/Components";
import { FeedbackDialog } from "components/Dialogs";
import UnAuthenticatedMenu from "./UnAuthenticatedMenu/UnAuthenticatedMenu";

// assets
import SBALogoImage from "assets/images/sba-logo-new.png";

const helpGuideLink ="https://sbaone.atlassian.net/wiki/external/OTc2MGMyOTU1ZmI3NGQ5MmE3NWEyMGU1ZGVlNTBkZGE";
  const feedbackLink =
    "https://sbaone.atlassian.net/servicedesk/customer/portal/2083";

export default function Header() {
  const navigate = useNavigate();
  const {
    device,
    setDevice,
    mobileMenuOpen,
    toggleMenu,
    closeMenu,
    menuComponent,
  } = useResponsiveStore();
  const isLoginEnabled = process.env.REACT_APP_SBS_FEATURE_LOGIN === "true";

  const { isAuthenticated, logoutOkta } = useAuthStore();

  // const [feedbackOpen, setFeedbackOpen] = useState<boolean>(false);

  const handleLogout = () => {
    closeMenu();
    logoutOkta();

    toast.success(
      <div>
        <h3>You have successfully signed out</h3>
        <p style={{ marginTop: "8px" }}>See you next time!</p>
      </div>,
      {
        position: "bottom-left",
      }
    );

    // navigate("/");
  };

  // header/mobile state
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 800 && device === "desktop") {
        setDevice("mobile");
      } else if (window.innerWidth >= 800 && device === "mobile") {
        setDevice("desktop");
        closeMenu();
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [window.innerWidth]);

  // handle mobile menu overlay and escaping
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.classList.add(styles["no-scroll"]);
    } else {
      document.body.classList.remove(styles["no-scroll"]);
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && mobileMenuOpen) {
        closeMenu();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [mobileMenuOpen]);

  return (
    <header className={styles["header-root"]}>
      <GovBanner className={styles["gov-banner"]} id='header-gov-banner-container' aria-label="Official Government Website" />
      {/* overlay */}
      {mobileMenuOpen && (
        <div onClick={closeMenu} className={styles["overlay"]}></div>
      )}

      <div className={styles["container"]}>
        <div className={styles["left"]}>
          <a className={styles["logo"]} href="https://www.sba.gov/" target="_blank" rel="noopener noreferrer">
            <img
              src={SBALogoImage}
              alt="Small Business Administration Logo"
            />
          </a>

          <hr className={styles["divider"]} />

          <Link to="/" className={styles["home"]}>
            <SBSLogo />
            <h2>Small Business Search</h2>
          </Link>
        </div>

        {/* show menu on desktop or when mobile menu is open */}
        {(device === "desktop" || mobileMenuOpen) && (
          <FocusLock disabled={!mobileMenuOpen}>
            <div className={styles["right"]}>
              {device === "mobile" && (
                <>
                  <div className={styles["close"]}>
                    <IconButton
                      type="button"
                      Icon={<Icon.Close />}
                      size="sm"
                      onClick={closeMenu}
                      aria-label="Close menu"
                    />
                  </div>

                  {/* add in page nav to mobile menu*/}
                  {menuComponent && <div className={styles["menu-component"]}>{menuComponent}</div>}
                </>
              )}

              {/* show feedback before login on desktop to ensure tabbing makes logical sense */}
              {device === "desktop" && (
                <div className={styles["help-guide-container"]}>
                  <RoundedAnchorButton
                    variant="ghost"
                    className={styles["btn-help-anchor"]}
                    target={"_blank"}
                    rel="noopener noreferrer"
                    href={helpGuideLink}
                    aria-label="Link to the SBS service desk"
                  >
                    <HelpGuide />
                    Help Guide
                  </RoundedAnchorButton>

                  <RoundedAnchorButton
                    variant="ghost"
                    className={styles["btn-help-anchor"]}
                    target={"_blank"}
                    rel="noopener noreferrer"
                    href={feedbackLink}
                    aria-label="Link to the SBS service desk"
                  >
                    <GetHelp />
                    Get help
                  </RoundedAnchorButton>
                </div>

                // <RoundedButton
                //   variant="ghost"
                //   className={styles["help-guide"]}
                //   onClick={() => {
                //     setFeedbackOpen(true)
                //     closeMenu()
                //   }}
                // >
                //   <Feedback />
                //   Give feedback
                // </RoundedButton>
              )}

              {isLoginEnabled && (
                <div className={styles["user-wrapper"]}>
                  {!isAuthenticated && <UnAuthenticatedMenu />}
                  {isAuthenticated && <Menu handleLogout={handleLogout} />}
                </div>
              )}

              {/* show feedback after login on mobile to ensure tabbing makes logical sense */}
              {device === "mobile" && (
                <>
                  <RoundedAnchorButton
                    variant="ghost"
                    className={styles["btn-help-anchor"]}
                    target={"_blank"}
                    rel="noopener noreferrer"
                    href={helpGuideLink}
                    aria-label="Link to the SBS service desk"
                  >
                    <HelpGuide />
                    Help Guide
                  </RoundedAnchorButton>
                  
                  <RoundedAnchorButton
                    variant="ghost"
                    className={styles["help-guide"]}
                    target={"_blank"}
                    rel="noopener noreferrer"
                    href={feedbackLink}
                    onClick={closeMenu}
                    aria-label="Link to the SBS service desk"
                  >
                    <GetHelp />
                    Get help
                  </RoundedAnchorButton>
                </>

                // <RoundedButton
                //   variant="ghost"
                //   className={styles["help-guide"]}
                //   onClick={() => {
                //     setFeedbackOpen(true)
                //     closeMenu()
                //   }}
                // >
                //   <Feedback />
                //   Give feedback
                // </RoundedButton>
              )}
            </div>
          </FocusLock>
        )}

        {/* show mobile nav button */}
        {device === 'mobile' && (
          <Button className={styles["mobile-nav"]} onClick={toggleMenu} type="button"><IoMenuSharp /><p>Menu</p></Button>
        )}
      </div>
      
      {/* {feedbackOpen && <FeedbackDialog open={feedbackOpen} setOpen={setFeedbackOpen} />} */}
    </header>
  );
}